import { ClipboardCheck, Clock } from 'lucide-react';

import { EXAM_TYPE_LABEL } from '@/features/teacher/exams/constants';
import { formatTimeRange } from '@/lib/format';
import type { OverviewExam } from '@/types/teacherDashboard';

interface ScheduleExamListProps {
  exams: OverviewExam[];
}

/**
 * The papers sat on the day, at the head of the dashboard's schedule card.
 *
 * A paper is not a period — it has no period number or room on the timetable,
 * and often an exam day has no lessons at all — so it gets its own rows rather
 * than being dressed up as a slot. Renders nothing on a day without papers.
 */
export function ScheduleExamList({ exams }: ScheduleExamListProps) {
  if (exams.length === 0) return null;

  return (
    <ul className="flex flex-col gap-3" aria-label="Exams on this day">
      {exams.map((exam) => (
        <li
          key={exam.uuid}
          className="flex flex-col gap-3 rounded-card border border-line bg-surface p-2.5 sm:flex-row sm:items-center sm:justify-between sm:p-2"
        >
          <div className="flex min-w-0 items-start gap-3 sm:items-center">
            <span
              className="mt-0.5 grid h-10 w-10 shrink-0 place-items-center rounded-card bg-warn-bg text-warn sm:mt-0"
              aria-hidden
            >
              <ClipboardCheck className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-baseline gap-1 sm:gap-1.5">
                <h3 className="text-sm font-bold text-ink">{exam.subject.name}</h3>
                <span className="text-xs font-semibold text-ink-3">({exam.displayName})</span>
              </div>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-sm font-medium text-ink-3">
                <span className="inline-flex shrink-0 items-center gap-1">
                  <Clock className="h-3 w-3 shrink-0" aria-hidden />
                  {formatTimeRange(exam.startTime, exam.endTime, 'Time not set')}
                </span>
                <span className="hidden sm:inline" aria-hidden>
                  ·
                </span>
                <span>
                  {exam.name} · {EXAM_TYPE_LABEL[exam.examType]} · out of {exam.maxMarks}
                </span>
              </p>
            </div>
          </div>
          <span className="pill shrink-0 self-start bg-warn-bg text-warn sm:self-auto">Exam</span>
        </li>
      ))}
    </ul>
  );
}
